import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Margin, Padding } from 'styled-components-spacing';
import { PageContent, Container, Heading, Col, Row } from 'components';
import { connect } from 'react-redux';
import { useAuthLock } from 'hooks';
import {
  loadIpmaCisDetailsCreator,
  removeIpmaCisDetailsCreator,
} from '../../../../store/actionsCreators';
import { Wrapper } from '../../../../components/UserList/UserList.styled';

const Details = ({ loadDetails, removeDetails, details, history }) => {
  const { t } = useTranslation();
  const { id } = useParams();
  const [renderAuthLock] = useAuthLock(history);

  useEffect(() => {
    loadDetails(id, history);

    return () => {
      removeDetails();
    };
  }, []);

  const renderLevelDomains = () => {
    return (
      <Wrapper>
        <Margin bottom="16">
          <Heading as="h3" size="h5">
            {t('page.user.cis.certificationBodies.certificateLevelDomains')}:
          </Heading>
        </Margin>
        {details.certificationBodyLevelDomains &&
          details.certificationBodyLevelDomains.map((item, index) => (
            <Margin key={index} bottom="8" style={{ 'padding-left': '20px' }}>
              <Margin bottom="4">
                <Heading as="h4" size="h6">
                  {item.certificateLevelDomain.designation}
                </Heading>
              </Margin>

              {` ${t('page.user.cis.certificationBodies.level')}: ${
                item.certificateLevelDomain.level.name
              }, ${t('page.user.cis.certificationBodies.domain')}: ${
                item.certificateLevelDomain.domain.name
              }`}
            </Margin>
          ))}
      </Wrapper>
    );
  };

  const renderContactDetails = () => (
    <Padding all="20" className="bg-gray-100">
      <Margin bottom="16">
        <Heading as="h3" size="h6">
          {t('page.user.cis.certificationBodies.contact')}:
        </Heading>
      </Margin>

      {details.telephone && (
        <Margin bottom="12">
          <a href={`tel:${details.telephone}`}>{details.telephone}</a>
        </Margin>
      )}
      {details.email && (
        <Margin bottom="12">
          <a href={`mailto:${details.email}`} className="link link--16 word-break">
            {details.email}
          </a>
        </Margin>
      )}
      {details.website && (
        <a href={details.website} className="link link--16 word-break">
          {details.website}
        </a>
      )}
    </Padding>
  );

  return (
    <>
      <PageContent>
        {renderAuthLock()}
        <Container>
          <Row>
            <Col size={{ xl: 2 / 3 }}>
              <Margin bottom="24">
                <Heading as="h1" size="h1" firstLine>
                  {details.name}
                </Heading>
              </Margin>
              {renderLevelDomains()}
            </Col>
            <Margin as={Col} size={{ xl: 1 / 3 }} top={{ xs: 32, xl: 0 }}>
              {renderContactDetails()}
            </Margin>
          </Row>
        </Container>
      </PageContent>
    </>
  );
};

Details.propTypes = {
  loadDetails: PropTypes.func.isRequired,
  removeDetails: PropTypes.func.isRequired,
  history: PropTypes.shape({
    replace: PropTypes.func,
  }).isRequired,
  details: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    initials: PropTypes.string,
    telephone: PropTypes.string,
    website: PropTypes.string,
    email: PropTypes.string,
    certificationBodyLevelDomains: PropTypes.arrayOf(
      PropTypes.shape({
        certificateLevelDomain: PropTypes.shape({
          id: PropTypes.number,
          designation: PropTypes.string,
          level: PropTypes.shape({
            id: PropTypes.number,
            name: PropTypes.string,
          }),
          domain: PropTypes.shape({
            id: PropTypes.number,
            name: PropTypes.string,
          }),
        }),
      }),
    ),
  }),
};

Details.defaultProps = {
  details: {
    id: null,
    name: '',
    initials: '',
    telephone: '',
    website: '',
    email: '',
    certificationBodyLevelDomains: [],
  },
};

const mapStateToProps = ({ ipma }) => ({
  details: ipma.cis.certificationBodies.details,
});

const mapDispatchToProps = (dispatch) => ({
  loadDetails: (payload, history) =>
    dispatch(loadIpmaCisDetailsCreator('certificationBodies', payload, history)),
  removeDetails: () => dispatch(removeIpmaCisDetailsCreator('certificationBodies')),
});

export default connect(mapStateToProps, mapDispatchToProps)(Details);
